import { useState, useEffect, useMemo, useCallback } from "react";
import { 
  Calendar, Clock, RefreshCw, GripVertical, User, MapPin, Car, 
  AlertTriangle, Check, X, ChevronLeft, ChevronRight, Sparkles,
  ArrowLeft, Star, ThumbsUp, Navigation, Phone, Mail, Send
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Créneaux horaires de 8h à 21h
const TIME_SLOTS = [];
for (let h = 8; h <= 21; h++) {
  TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:00`);
  if (h < 21) TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:30`);
}

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

// Distances estimées entre départements IDF
const estimateTravelTime = (dpt1, dpt2) => {
  if (!dpt1 || !dpt2) return null;
  const dptDistances = {
    '75': { '75': 20, '92': 25, '93': 30, '94': 30, '77': 60, '78': 45, '91': 50, '95': 40 },
    '92': { '75': 25, '92': 20, '93': 35, '94': 40, '77': 70, '78': 35, '91': 55, '95': 45 },
    '93': { '75': 30, '92': 35, '93': 20, '94': 35, '77': 50, '78': 55, '91': 55, '95': 35 },
    '94': { '75': 30, '92': 40, '93': 35, '94': 20, '77': 45, '78': 60, '91': 40, '95': 50 },
    '77': { '75': 60, '92': 70, '93': 50, '94': 45, '77': 30, '78': 80, '91': 50, '95': 70 },
    '78': { '75': 45, '92': 35, '93': 55, '94': 60, '77': 80, '78': 25, '91': 50, '95': 50 },
    '91': { '75': 50, '92': 55, '93': 55, '94': 40, '77': 50, '78': 50, '91': 25, '95': 65 },
    '95': { '75': 40, '92': 45, '93': 35, '94': 50, '77': 70, '78': 50, '91': 65, '95': 25 },
  };
  const d1 = String(dpt1).substring(0, 2);
  const d2 = String(dpt2).substring(0, 2);
  return dptDistances[d1]?.[d2] || 45;
};

// Panneau des préconisations San-ia
const SaniaRecommendations = ({ recommendations, onSelectSlot, loading }) => {
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 border border-purple-500/30 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-purple-400 animate-pulse" />
          <span className="font-semibold text-purple-300">San-ia analyse les créneaux...</span>
        </div>
        <div className="flex justify-center py-4">
          <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (!recommendations || recommendations.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 border border-purple-500/30 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-purple-400" />
        <span className="font-semibold text-purple-300">Préconisations San-ia</span>
        <span className="text-xs text-purple-400/70 ml-auto">Optimisé par proximité géographique</span>
      </div>
      
      <div className="space-y-3">
        {recommendations.map((rec, idx) => (
          <div 
            key={rec.date}
            className={`p-3 rounded-lg border transition-all ${
              idx === 0 
                ? 'bg-green-500/20 border-green-500/50' 
                : 'bg-slate-800/50 border-slate-700/50 hover:border-purple-500/50'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {idx === 0 && <Star className="w-4 h-4 text-green-400" />}
                <span className="font-medium text-white">{rec.jour} {rec.date_fr}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">
                  {rec.rdvs_existants} RDV existant{rec.rdvs_existants > 1 ? 's' : ''}
                </span>
              </div>
              <div className={`text-sm font-bold ${
                rec.meilleur_score >= 90 ? 'text-green-400' :
                rec.meilleur_score >= 70 ? 'text-amber-400' : 'text-slate-400'
              }`}>
                Score: {rec.meilleur_score}%
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {rec.creneaux_recommandes.slice(0, 3).map((slot, slotIdx) => (
                <button
                  key={slot.heure}
                  onClick={() => onSelectSlot(rec.date, slot.heure)}
                  className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-all ${
                    slotIdx === 0 && idx === 0
                      ? 'bg-green-500 hover:bg-green-400 text-white font-medium'
                      : 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                  }`}
                >
                  <Clock className="w-3 h-3" />
                  {slot.heure}
                  {slot.travel_info && (
                    <span className="text-xs opacity-75 flex items-center gap-1">
                      <Car className="w-3 h-3" />
                      {slot.travel_info.minutes}min
                    </span>
                  )}
                </button>
              ))}
            </div>
            
            {rec.creneaux_recommandes[0]?.reason && (
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                <Navigation className="w-3 h-3" />
                {rec.creneaux_recommandes[0].reason}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Modal de confirmation avec options SMS/Email
const ConfirmationModal = ({ client, selectedSlot, onConfirm, onCancel, confirming }) => {
  const [sendSms, setSendSms] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(client?.telephones?.[0] || "");
  const [selectedEmailAddr, setSelectedEmailAddr] = useState(client?.emails?.[0] || "");

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const handleConfirm = () => {
    onConfirm({
      sendSms,
      sendEmail,
      selectedPhone,
      selectedEmailAddr
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Check className="w-5 h-5 text-green-400" />
            Confirmer le repositionnement
          </h3>
          <button onClick={onCancel} className="p-1 hover:bg-slate-700 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-4 space-y-4">
          {/* Récapitulatif */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <p className="text-sm text-slate-400 mb-2">Nouveau rendez-vous</p>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-2xl font-bold text-green-400">{formatDate(selectedSlot?.date)}</p>
                <p className="text-lg text-white">à {selectedSlot?.time}</p>
              </div>
              <div className="flex-1 text-right">
                <p className="font-medium text-white">{client?.nom} {client?.prenom}</p>
                <p className="text-sm text-slate-400">Technicien: {client?.technicien || "ANTHONY"}</p>
              </div>
            </div>
          </div>

          {/* Options SMS/Email */}
          <div>
            <p className="text-sm text-slate-400 mb-3 flex items-center gap-2">
              <Send className="w-4 h-4" />
              Envoyer une confirmation au client
            </p>
            
            <div className="space-y-3">
              {/* Option SMS */}
              <div className={`p-3 rounded-lg border transition-all ${sendSms ? 'bg-green-500/10 border-green-500/50' : 'bg-slate-800/50 border-slate-700'}`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendSms}
                    onChange={(e) => setSendSms(e.target.checked)}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-green-500"
                  />
                  <Phone className="w-5 h-5 text-green-400" />
                  <span className="font-medium">Envoyer un SMS</span>
                </label>
                {sendSms && (
                  <div className="mt-3 ml-8">
                    <select
                      value={selectedPhone}
                      onChange={(e) => setSelectedPhone(e.target.value)}
                      className="w-full p-2 bg-slate-800 border border-slate-600 rounded-lg text-sm"
                    >
                      <option value="">Choisir un numéro...</option>
                      {client?.telephones?.map((tel, idx) => (
                        <option key={idx} value={tel}>{tel}</option>
                      ))}
                      {(!client?.telephones || client.telephones.length === 0) && (
                        <option value="">Aucun numéro disponible</option>
                      )}
                    </select>
                  </div>
                )}
              </div>

              {/* Option Email */}
              <div className={`p-3 rounded-lg border transition-all ${sendEmail ? 'bg-blue-500/10 border-blue-500/50' : 'bg-slate-800/50 border-slate-700'}`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-blue-500"
                  />
                  <Mail className="w-5 h-5 text-blue-400" />
                  <span className="font-medium">Envoyer un Email</span>
                </label>
                {sendEmail && (
                  <div className="mt-3 ml-8">
                    <select
                      value={selectedEmailAddr}
                      onChange={(e) => setSelectedEmailAddr(e.target.value)}
                      className="w-full p-2 bg-slate-800 border border-slate-600 rounded-lg text-sm"
                    >
                      <option value="">Choisir un email...</option>
                      {client?.emails?.map((email, idx) => (
                        <option key={idx} value={email}>{email}</option>
                      ))}
                      {(!client?.emails || client.emails.length === 0) && (
                        <option value="">Aucun email disponible</option>
                      )}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirming}
            className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-500 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {confirming ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Check className="w-5 h-5" />
            )}
            Confirmer le RDV
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant principal de repositionnement
export default function RepositionnerRdvView({ client, onClose, onSuccess }) {
  const [rdvs, setRdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRecommendations, setLoadingRecommendations] = useState(true);
  const [recommendations, setRecommendations] = useState([]);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1) + 7); // Semaine prochaine
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [selectedSlot, setSelectedSlot] = useState(null); // { date, time }
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [hoverSlot, setHoverSlot] = useState(null);

  const technicien = client?.technicien || "ANTHONY";
  const clientDpt = client?.dpt || client?.departement || "75";

  // Charger les RDV du calendrier
  const loadRdvs = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/api/calendrier/rdv?intervenant=${encodeURIComponent(technicien)}`);
      if (resp.ok) {
        const data = await resp.json();
        setRdvs(data.rdv || []);
      }
    } catch (e) {
      console.error("Erreur chargement RDV:", e);
    }
    setLoading(false);
  }, [technicien]);

  // Charger les préconisations San-ia
  const loadRecommendations = useCallback(async () => {
    setLoadingRecommendations(true);
    try {
      const resp = await authFetch(`${API}/api/calendrier/preconisations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: client?.client_id,
          technicien: technicien,
          departement: clientDpt,
          date_debut: weekStart.toISOString().split('T')[0],
          nb_jours: 5
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setRecommendations(data.top_recommendations || []);
      }
    } catch (e) {
      console.error("Erreur préconisations:", e);
    }
    setLoadingRecommendations(false);
  }, [client, technicien, clientDpt, weekStart]);

  useEffect(() => {
    loadRdvs();
    loadRecommendations();
  }, [loadRdvs, loadRecommendations]);

  // Jours de la semaine
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekStart]);

  const formatDateKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // RDV groupés par jour et heure
  const rdvGrid = useMemo(() => {
    const grid = {};
    weekDays.forEach(d => {
      const dateKey = formatDateKey(d);
      grid[dateKey] = {};
      TIME_SLOTS.forEach(t => grid[dateKey][t] = []);
    });
    
    rdvs.forEach((r, idx) => {
      if (grid[r.date]) {
        const heure = r.heure || `${9 + (idx % 8)}:00`;
        const slot = TIME_SLOTS.find(t => t <= heure) || TIME_SLOTS[0];
        if (grid[r.date][slot]) {
          grid[r.date][slot].push({ ...r, heure });
        }
      }
    });
    
    return grid;
  }, [rdvs, weekDays]);

  // Navigation semaine
  const goPrevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };

  const goNextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };

  // Sélectionner un créneau
  const handleSelectSlot = (dateKey, time) => {
    setSelectedSlot({ date: dateKey, time });
  };

  // Ouvrir la modal de confirmation
  const openConfirmModal = () => {
    if (selectedSlot) {
      setShowConfirmModal(true);
    }
  };

  // Confirmer le repositionnement
  const handleConfirm = async (options) => {
    if (!selectedSlot || !client) return;
    
    setConfirming(true);
    try {
      // selectedSlot.date est déjà au format YYYY-MM-DD
      const resp = await authFetch(`${API}/api/pipeline/client/${encodeURIComponent(client.client_id)}/repositionner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_date_r1: selectedSlot.date, // Format YYYY-MM-DD attendu par l'API
          new_heure_r1: selectedSlot.time,
          send_sms: options.sendSms,
          send_email: options.sendEmail,
          phone_number: options.selectedPhone,
          email_address: options.selectedEmailAddr
        })
      });
      
      if (resp.ok) {
        const result = await resp.json();
        console.log("✅ RDV repositionné:", result);
        setShowConfirmModal(false);
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } else {
        const error = await resp.json();
        console.error("Erreur repositionnement:", error);
        alert(`Erreur: ${error.detail || 'Erreur inconnue'}`);
      }
    } catch (e) {
      console.error("Erreur:", e);
      alert("Erreur lors du repositionnement");
    }
    setConfirming(false);
  };

  const isToday = (d) => d.toDateString() === new Date().toDateString();
  const isRecommended = (dateKey, time) => {
    return recommendations.some(r => 
      r.date === dateKey && 
      r.creneaux_recommandes.some(c => c.heure === time)
    );
  };
  const getRecommendationScore = (dateKey, time) => {
    const rec = recommendations.find(r => r.date === dateKey);
    if (!rec) return null;
    const slot = rec.creneaux_recommandes.find(c => c.heure === time);
    return slot?.score || null;
  };

  // Trouver les RDV adjacents pour le hover
  const findAdjacentRdvs = (dateKey, time) => {
    const dayRdvs = [];
    Object.entries(rdvGrid[dateKey] || {}).forEach(([t, rdvList]) => {
      rdvList.forEach(r => dayRdvs.push({ ...r, slotTime: t }));
    });
    dayRdvs.sort((a, b) => a.heure.localeCompare(b.heure));
    
    const timeMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
    let rdvBefore = null, rdvAfter = null;
    
    for (const r of dayRdvs) {
      const rMinutes = parseInt(r.heure.split(':')[0]) * 60 + parseInt(r.heure.split(':')[1]);
      if (rMinutes < timeMinutes) rdvBefore = r;
      else if (rMinutes > timeMinutes && !rdvAfter) { rdvAfter = r; break; }
    }
    
    return { rdvBefore, rdvAfter };
  };

  return (
    <div className="fixed inset-0 bg-slate-900/95 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg text-slate-400"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Calendar className="w-6 h-6 text-amber-400" />
                Repositionner le RDV
              </h1>
              <p className="text-sm text-slate-400">
                {client?.nom} {client?.prenom} • Technicien: {technicien} • Dpt: {clientDpt}
              </p>
            </div>
          </div>
          
          {selectedSlot && (
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-slate-400">Nouveau créneau sélectionné</p>
                <p className="text-lg font-bold text-amber-400">
                  {selectedSlot.date.split('-').reverse().join('/')} à {selectedSlot.time}
                </p>
              </div>
              <button
                onClick={openConfirmModal}
                className="px-6 py-3 bg-green-600 hover:bg-green-500 text-white rounded-lg font-medium flex items-center gap-2"
                data-testid="confirm-reposition-btn"
              >
                <Check className="w-5 h-5" />
                Confirmer
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Panneau gauche: Préconisations San-ia */}
          <div className="lg:col-span-1 space-y-4">
            <SaniaRecommendations 
              recommendations={recommendations}
              onSelectSlot={handleSelectSlot}
              loading={loadingRecommendations}
            />
            
            {/* Info client actuel */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-blue-400" />
                Client à repositionner
              </h3>
              <div className="space-y-2 text-sm">
                <p className="text-slate-300">{client?.nom} {client?.prenom}</p>
                <p className="text-slate-400 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Département {clientDpt}
                </p>
                {client?.date_r1 && (
                  <p className="text-slate-400 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> RDV actuel: {client.date_r1}
                  </p>
                )}
                {client?.telephones?.length > 0 && (
                  <p className="text-slate-400 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {client.telephones[0]}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Panneau droit: Calendrier semaine */}
          <div className="lg:col-span-3">
            <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
              {/* Navigation */}
              <div className="bg-slate-700/50 px-4 py-3 border-b border-slate-600 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button onClick={goPrevWeek} className="p-1.5 hover:bg-slate-600 rounded">
                    <ChevronLeft className="w-5 h-5 text-slate-300" />
                  </button>
                  <button onClick={goNextWeek} className="p-1.5 hover:bg-slate-600 rounded">
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  </button>
                  <span className="text-white font-medium ml-2">
                    {weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} - {
                      new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                    }
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-green-500/50 border border-green-500" />
                    Recommandé
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-amber-500/50 border border-amber-500" />
                    Sélectionné
                  </div>
                </div>
              </div>

              {/* Header jours */}
              <div className="flex border-b border-slate-600">
                <div className="w-16 flex-shrink-0 bg-slate-900/50" />
                {weekDays.map((d, i) => {
                  const dateKey = formatDateKey(d);
                  const dayRdvCount = Object.values(rdvGrid[dateKey] || {}).flat().length;
                  const isWeekend = i >= 5;
                  
                  return (
                    <div 
                      key={i} 
                      className={`flex-1 text-center py-2 border-l border-slate-700 ${
                        isToday(d) ? 'bg-amber-500/20' : 
                        isWeekend ? 'bg-slate-900/50 opacity-50' : 'bg-slate-700/30'
                      }`}
                    >
                      <div className="text-xs text-slate-400">{JOURS[i]}</div>
                      <div className={`text-lg font-bold ${isToday(d) ? 'text-amber-400' : 'text-white'}`}>
                        {d.getDate()}
                      </div>
                      {dayRdvCount > 0 && (
                        <div className="text-xs text-amber-400">{dayRdvCount} RDV</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Grille horaire */}
              {loading ? (
                <div className="flex justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
                </div>
              ) : (
                <div className="flex max-h-[500px] overflow-y-auto">
                  {/* Colonne heures */}
                  <div className="w-16 flex-shrink-0 bg-slate-900/50 sticky left-0 z-10">
                    {TIME_SLOTS.filter(t => t.endsWith(':00')).map(time => (
                      <div 
                        key={time} 
                        className="h-[60px] flex items-center justify-center text-xs text-amber-400 font-medium border-b border-slate-700/30"
                      >
                        {time}
                      </div>
                    ))}
                  </div>

                  {/* Colonnes jours */}
                  {weekDays.map((d, dayIdx) => {
                    const dateKey = formatDateKey(d);
                    const isWeekend = dayIdx >= 5;
                    
                    return (
                      <div key={dayIdx} className={`flex-1 border-l border-slate-700 min-w-[100px] ${isWeekend ? 'opacity-30 pointer-events-none' : ''}`}>
                        {TIME_SLOTS.filter(t => t.endsWith(':00')).map(time => {
                          const slotRdvs = rdvGrid[dateKey]?.[time] || [];
                          const isSelected = selectedSlot?.date === dateKey && selectedSlot?.time === time;
                          const isRec = isRecommended(dateKey, time);
                          const score = getRecommendationScore(dateKey, time);
                          const isHovered = hoverSlot === `${dateKey}-${time}`;
                          const { rdvBefore, rdvAfter } = isHovered ? findAdjacentRdvs(dateKey, time) : {};
                          
                          return (
                            <div
                              key={time}
                              onClick={() => !isWeekend && handleSelectSlot(dateKey, time)}
                              onMouseEnter={() => setHoverSlot(`${dateKey}-${time}`)}
                              onMouseLeave={() => setHoverSlot(null)}
                              className={`h-[60px] border-b border-slate-700/30 p-1 cursor-pointer transition-all relative ${
                                isSelected ? 'bg-amber-500/30 ring-2 ring-amber-500' :
                                isRec ? 'bg-green-500/10 hover:bg-green-500/20' :
                                isHovered ? 'bg-slate-700/50' :
                                'bg-slate-800/30 hover:bg-slate-700/30'
                              }`}
                            >
                              {/* Indicateur recommandé */}
                              {isRec && !isSelected && (
                                <div className="absolute top-0.5 right-0.5">
                                  <Star className="w-3 h-3 text-green-400" />
                                </div>
                              )}
                              
                              {/* Badge score */}
                              {score && score >= 80 && !isSelected && (
                                <div className="absolute bottom-0.5 right-0.5 text-[10px] px-1 rounded bg-green-500/30 text-green-400">
                                  {score}%
                                </div>
                              )}
                              
                              {/* RDV existants */}
                              {slotRdvs.map(rdv => (
                                <div
                                  key={rdv.id}
                                  className={`${rdv.type === "R1" ? "bg-blue-500/80" : "bg-purple-500/80"} rounded p-1 text-xs mb-0.5`}
                                >
                                  <div className="text-white font-medium truncate">{rdv.client?.split(' ')[0]}</div>
                                  <div className="text-white/70 flex items-center gap-1">
                                    <Clock className="w-2 h-2" />{rdv.heure}
                                    {rdv.departement && <span>• {rdv.departement}</span>}
                                  </div>
                                </div>
                              ))}
                              
                              {/* Aperçu temps trajet au hover */}
                              {isHovered && !slotRdvs.length && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="text-xs text-slate-400 text-center">
                                    {rdvBefore && (
                                      <div className="flex items-center gap-1 text-amber-400">
                                        <Car className="w-3 h-3" />
                                        {estimateTravelTime(rdvBefore.departement, clientDpt)}min depuis {rdvBefore.departement}
                                      </div>
                                    )}
                                    {rdvAfter && (
                                      <div className="flex items-center gap-1 text-amber-400">
                                        <Car className="w-3 h-3" />
                                        {estimateTravelTime(clientDpt, rdvAfter.departement)}min vers {rdvAfter.departement}
                                      </div>
                                    )}
                                    {!rdvBefore && !rdvAfter && <span>Cliquez pour sélectionner</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Légende */}
              <div className="bg-slate-700/30 px-4 py-2 border-t border-slate-600 flex items-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-500 rounded" /> R1 existant</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 bg-purple-500 rounded" /> R2 existant</span>
                <span className="flex items-center gap-1"><Star className="w-3 h-3 text-green-400" /> Recommandé San-ia</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <ConfirmationModal
          client={client}
          selectedSlot={selectedSlot}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmModal(false)}
          confirming={confirming}
        />
      )}
    </div>
  );
}
